import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useManagedMembersContext } from '@/context/ManagedMembersContext';
import { MButton } from '@/components/ui/atoms/MButtons';
import { IconCircleCheck } from '@tabler/icons-react';
import { getMemberInfo } from '@/hooks/useMembers';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

interface AddMemberSuccessState {
  memberName: string;
  nickname?: string;
  invite?: boolean;
  isChildOrPet?: boolean;
  isFirstFamilyMember?: boolean;
}

const AddMemberSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchManagedMembers, managedMembers } = useManagedMembersContext();
  const state = (location.state || {}) as AddMemberSuccessState;
  const { memberName, nickname, invite, isChildOrPet, isFirstFamilyMember } = state;
  const memberInfo = getMemberInfo(true);

  React.useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_ADD_MEMBER_COMPLETE,
      ...(isFirstFamilyMember && { action: 'first_family_member_added' }),
    });
  }, [memberInfo?.id, isFirstFamilyMember]);

  return (
    <div className="flex flex-col items-stretch w-screen min-h-screen bg-white curvy-gradient-profile-success">
      <div style={{ display: 'flex', height: 48, padding: '12px 24px', alignItems: 'center', gap: 10, flexShrink: 0, alignSelf: 'stretch' }} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, padding: 24, marginTop: 64, flex: 1, width: '100%' }}>
        <div>
          <IconCircleCheck size={72} stroke={2.2} color="#C8B7E0" />
        </div>
        <div style={{ textAlign: 'center' }} className='body-xl-bold'>
          {nickname || memberName} has been added to your Maatra Family
        </div>
        {invite ? (
          <div style={{ textAlign: 'center',lineHeight: 1.5 }} className='manageAccessEmptyText'>
            We’ve also sent them an invite to join and manage their own profile.
          </div>
        ) : (
          isChildOrPet ? (
            <div style={{ textAlign: 'center', lineHeight: 1.5 }} className='manageAccessEmptyText'>
              You can now manage health records and reminders on their behalf
            </div>
          ) : (
            <div style={{ textAlign: 'center', lineHeight: 1.5 }} className='manageAccessEmptyText'>
              You can invite them anytime if you'd like them to manage their own profile.
            </div>
          )
        )}
        <div className="w-full mt-auto pb-6 flex flex-col gap-4">
          <MButton
            variant="outline"
            size="lg"
            className="w-full"
            onClick={async () => {
              await fetchManagedMembers(true);
              navigate('/profile/family/add-member', { replace: true });
            }}
          >
             Add another member
          </MButton>
          <MButton
            variant="filled"
            size="lg"
            className="w-full"
            onClick={async () => {
              await fetchManagedMembers(true);
              navigate('/profile/family');
            }}
          >
            Done
          </MButton>
        </div>
      </div>
    </div>
  );
};

export default AddMemberSuccess;
